import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { useT } from "./i18n";
import { trackCtaClick, trackEvent } from "./analytics";
import LeadNoteForm from "./components/LeadNoteForm";

const CALENDLY_URL = "https://calendly.com/prophero/30min";
const CALENDLY_PARAMS = {
  hide_gdpr_banner: "1",
  background_color: "ffffff",
  text_color: "0A1428",
  primary_color: "1E3A8A",
};

function buildCalendlyUrl(lang) {
  const params = new URLSearchParams({
    ...CALENDLY_PARAMS,
    locale: lang === "es" ? "es" : "en",
    embed_domain: typeof window !== "undefined" ? window.location.hostname : "",
    embed_type: "PopupWidget",
  });
  return `${CALENDLY_URL}?${params.toString()}`;
}

const BookingContext = createContext({
  open: () => {},
  bookedOnce: false,
});

export function useBooking() {
  return useContext(BookingContext);
}

export function BookingProvider({ children }) {
  const { t, lang } = useT();
  const [open, setOpen] = useState(false);
  const [bookedOnce, setBookedOnce] = useState(false);
  const [tab, setTab] = useState("schedule"); // "schedule" | "note"
  const iframeKeyRef = useRef(0);

  // Listen for Calendly's `event_scheduled` → close popup + mark booked.
  useEffect(() => {
    const onMsg = (e) => {
      if (
        !(
          e?.origin?.includes("calendly.com") &&
          typeof e?.data === "object" &&
          e?.data?.event?.indexOf("calendly.") === 0
        )
      )
        return;
      if (e.data.event === "calendly.event_scheduled") {
        setBookedOnce(true);
        setOpen(false);
        trackEvent("booking_confirmed", { source: "calendly" });
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);

  // ESC closes.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // Scroll lock: `scrollbar-gutter: stable` (in index.css) already reserves
  // the scrollbar's gutter permanently, so setting `overflow: hidden` here no
  // longer causes any horizontal content shift. iOS Safari still needs the
  // touchmove trap below.
  useEffect(() => {
    if (typeof document === "undefined" || !open) return;
    const html = document.documentElement;
    const body = document.body;
    const prev = {
      bodyOverflow: body.style.overflow,
      htmlOverflow: html.style.overflow,
    };
    body.style.overflow = "hidden";
    html.style.overflow = "hidden";

    const preventTouch = (e) => {
      const target = e.target;
      if (
        target &&
        target.closest &&
        target.closest('[data-testid="calendly-popup-card"]')
      )
        return;
      e.preventDefault();
    };
    document.addEventListener("touchmove", preventTouch, { passive: false });

    return () => {
      body.style.overflow = prev.bodyOverflow;
      html.style.overflow = prev.htmlOverflow;
      body.style.paddingRight = prev.bodyPR;
      document.removeEventListener("touchmove", preventTouch);
    };
  }, [open]);

  const openBooking = useCallback((sourceOrEvent, buttonTextArg) => {
    // Accept either `openBooking("hero_schedule_consultation", "Schedule a Consultation")`
    // or a plain click handler shape (e.g. `onClick={openBooking}`) where the
    // first argument is a React event.
    const source =
      typeof sourceOrEvent === "string" ? sourceOrEvent : undefined;
    const buttonText =
      typeof buttonTextArg === "string" ? buttonTextArg : undefined;
    if (source) {
      trackCtaClick(source, buttonText);
    }
    trackEvent("calendly_open", {
      source: source || "unknown",
    });
    iframeKeyRef.current += 1;
    setTab("schedule");
    setOpen(true);
  }, []);

  const calendlySrc = buildCalendlyUrl(lang);

  return (
    <BookingContext.Provider value={{ open: openBooking, bookedOnce }}>
      {children}
      {open && (
        <div
          data-testid="calendly-popup-root"
          className="fixed inset-0 z-[100] pointer-events-none"
          aria-modal="true"
          role="dialog"
          aria-label={t("contact.calendly.title")}
        >
          {/* Transparent click-catcher — no backdrop dim */}
          <button
            type="button"
            aria-label="Close scheduler"
            data-testid="calendly-popup-dismiss"
            onClick={() => setOpen(false)}
            className="absolute inset-0 w-full h-full bg-transparent cursor-default pointer-events-auto outline-none"
          />
          <div className="absolute inset-0 flex items-center justify-center p-1.5 sm:p-3 md:p-6 pointer-events-none">
            <div
              data-testid="calendly-popup-card"
              className="pointer-events-auto relative w-[98%] h-[88vh] max-h-[720px] md:w-[80%] md:h-[92vh] md:max-h-[900px] lg:w-full lg:max-w-[1040px] lg:h-[720px] lg:max-h-[90vh] bg-white rounded-2xl md:rounded-3xl shadow-[0_40px_100px_-20px_rgba(10,20,40,0.45)] overflow-hidden flex flex-col animate-[fadeUp_0.35s_cubic-bezier(0.16,1,0.3,1)]"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Tab bar with inline close button (X matches EN toggle style) */}
              <div
                data-testid="booking-tabs"
                className="flex items-center justify-between gap-6 px-5 md:px-8 pt-4 md:pt-5 pb-2 border-b border-black/5"
              >
                <div className="flex items-center gap-6 text-[13px] font-semibold tracking-[-0.01em]">
                  {[
                    { key: "schedule", label: t("leadNote.tab.schedule") },
                    { key: "note", label: t("leadNote.tab.note") },
                  ].map((it) => {
                    const active = tab === it.key;
                    return (
                      <button
                        key={it.key}
                        type="button"
                        data-testid={`booking-tab-${it.key}`}
                        onClick={() => setTab(it.key)}
                        className={`pb-2 outline-none ${active ? "text-[#0A1428]" : "text-[#86868B] hover:text-[#0A1428]"}`}
                        style={{ transition: "color 300ms cubic-bezier(0.16, 1, 0.3, 1)" }}
                      >
                        {it.label}
                      </button>
                    );
                  })}
                </div>
                <button
                  onClick={() => setOpen(false)}
                  aria-label="Close"
                  data-testid="calendly-popup-close"
                  className="inline-flex items-center justify-center min-w-[36px] px-2.5 py-1 rounded-full text-[11px] font-normal text-[#515154] border border-[#E5E7EB] hover:text-[#0A0A0A] mb-2"
                  style={{ transition: "color 300ms cubic-bezier(0.16, 1, 0.3, 1)" }}
                >
                  <X className="w-3.5 h-3.5" strokeWidth={1.75} />
                </button>
              </div>

              {/* Content area */}
              <div className="flex-1 w-full relative overflow-hidden bg-white">
                {tab === "schedule" && (
                  <iframe
                    key={`${lang}-${iframeKeyRef.current}`}
                    data-testid="calendly-iframe"
                    title={t("contact.calendly.title")}
                    src={calendlySrc}
                    className="absolute top-0 left-0 border-0 bg-white w-[117%] h-[117%] origin-top-left scale-[0.855] md:w-full md:h-full md:scale-100"
                    allow="camera; microphone; fullscreen"
                  />
                )}
                {tab === "note" && (
                  <div
                    data-testid="lead-note-panel"
                    className="absolute inset-0 overflow-y-auto px-5 md:px-8 py-5 md:py-6"
                  >
                    <header className="mb-4 md:mb-5">
                      <h2 className="text-[18px] md:text-[20px] font-semibold text-[#0A1428] tracking-[-0.01em]">
                        {t("leadNote.title")}
                      </h2>
                      <p className="mt-1 text-[13px] text-[#515154] leading-relaxed">
                        {t("leadNote.subtitle")}
                      </p>
                    </header>
                    <LeadNoteForm />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </BookingContext.Provider>
  );
}
